/**
 * Authenticated fetch wrapper.
 *
 * - Attaches the stored id token as the Bearer header.
 * - Refreshes proactively if the token is past its stored expiry.
 * - On a 401 response, tries one refresh + retry. If the refresh also
 *   fails (or there's no refresh token), it clears local auth storage
 *   and dispatches the global `webportal:auth-logout` event so the
 *   AuthProvider can flip its React state and the router can send the
 *   user back to /login.
 *
 * All API client modules go through this wrapper so the auth-expired
 * behaviour is consistent regardless of which endpoint surfaced the 401.
 */
import {
  AUTH_LOGOUT_EVENT,
  clearAllAuth,
  dispatchAuthLogout,
  getActiveAccountId,
  getAuthToken,
  getRefreshToken,
  isTokenExpired,
  setAuthToken,
  setRefreshToken,
  setTokenExpiryFromSeconds,
} from '../auth';
import { refreshSession, AuthError } from './auth';

/** Disable inflight-refresh dedup in tests by passing a fresh signal. */
let inflightRefresh: Promise<boolean> | null = null;

async function attemptRefresh(): Promise<boolean> {
  if (inflightRefresh) return inflightRefresh;

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    // Nothing we can do — caller's choice whether to treat as "logged out".
    return false;
  }

  inflightRefresh = (async () => {
    try {
      const result = await refreshSession(refreshToken);
      setAuthToken(result.token);
      if (result.refreshToken) setRefreshToken(result.refreshToken);
      setTokenExpiryFromSeconds(result.expiresIn);
      return true;
    } catch (err) {
      // 401/403 from refresh → user must sign in fresh.
      if (err instanceof AuthError && (err.status === 401 || err.status === 403)) {
        return false;
      }
      // Network or 5xx — don't log the operator out; let the next try
      // succeed.
      throw err;
    } finally {
      inflightRefresh = null;
    }
  })();

  return inflightRefresh;
}

function logoutAndDispatch(reason: 'token-expired' | 'refresh-failed'): void {
  clearAllAuth();
  dispatchAuthLogout({ reason });
}

function attachAuth(init: RequestInit | undefined, token: string | null): RequestInit {
  const headers = new Headers(init?.headers ?? {});
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  if (!headers.has('Content-Type') && init?.body) {
    headers.set('Content-Type', 'application/json');
  }
  return { ...init, headers };
}

export interface AuthFetchOptions extends RequestInit {
  /** Skip the 401 → refresh → retry dance. Used by the refresh endpoint itself. */
  skipAuthRefresh?: boolean;
}

/**
 * Tenant isolation: stamp the ACTIVE workspace onto every API call as
 * `?account=<id>`. The backend's allowedReposForRequester narrows to this
 * account when present, so switching the workspace selector actually scopes the
 * data (epics, analytics, catalog, …) to that workspace — instead of showing
 * every account the user belongs to. An explicit `account` on the caller's URL
 * always wins; a Request object or an unparseable URL is left untouched.
 */
function withActiveAccount(input: RequestInfo | URL): RequestInfo | URL {
  const accountId = getActiveAccountId();
  if (!accountId) return input;
  if (typeof input !== 'string' && !(input instanceof URL)) return input; // Request — leave as-is
  try {
    const isAbsolute = typeof input !== 'string' || /^https?:/i.test(input);
    const url = new URL(String(input), window.location.origin);
    if (!url.searchParams.has('account')) url.searchParams.set('account', accountId);
    return isAbsolute ? url.toString() : url.pathname + url.search + url.hash;
  } catch {
    return input;
  }
}

export async function authFetch(
  input: RequestInfo | URL,
  init?: AuthFetchOptions,
): Promise<Response> {
  const { skipAuthRefresh, ...rest } = init ?? {};
  const target = withActiveAccount(input);

  // Proactive refresh: if the token expired and we have a refresh token,
  // try to refresh before sending the request.
  if (!skipAuthRefresh && isTokenExpired()) {
    const ok = await attemptRefresh();
    if (!ok && getRefreshToken()) {
      // Refresh token was present but rejected — log the user out.
      logoutAndDispatch('refresh-failed');
    }
    // If no refresh token was stored, we fall through and let the server
    // 401 → handler below kick in (which will also clear state).
  }

  const token = getAuthToken();
  const response = await fetch(target, attachAuth(rest, token));

  if (response.status !== 401 || skipAuthRefresh) return response;

  // 401 path — try a single refresh + retry.
  const refreshed = await attemptRefresh();
  if (!refreshed) {
    logoutAndDispatch('token-expired');
    return response;
  }

  const retryToken = getAuthToken();
  return fetch(target, attachAuth(rest, retryToken));
}

// Re-export the event name + types so consumers can subscribe without
// reaching into the auth module directly.
export { AUTH_LOGOUT_EVENT };
